import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, CircleDollarSign, Copy } from 'lucide-react';
import {
  BatchLifecycleStepper,
  BatchStatusPill,
  DisbursementCard,
  SignedAmount,
  UpcomingAdjustmentCard,
} from '@/components/AdvanceCod';
import BatchOrdersPaginated from '@/components/AdvanceCod/BatchOrdersPaginated';
import { ROUTE } from '@/config';
import { FinanceTab } from '@/types';
import { useFetchAdvanceBatchByUID } from '@/hooksApi';
import { formatDateByDayjs, formatWithThousandSeparator } from '@/utils';

/**
 * Advance batch detail (DESIGN §4 + v2.1, session-12 rework): sub-header →
 * lifecycle stepper → amount summary → disbursement → Upcoming Adjustment →
 * excluded notice, plus a paginated Orders tab (batches can hold 500 orders
 * — rows come from GET /batches/{uid}/orders, never embedded).
 */
const AdvanceBatchSingle = () => {
  const { t } = useTranslation();
  const { uid } = useParams();
  const navigate = useNavigate();
  const [activeSegment, setActiveSegment] = useState(0);
  const [excludedOnly, setExcludedOnly] = useState(false);
  const [copied, setCopied] = useState(false);
  const excludedNoticeRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useFetchAdvanceBatchByUID(uid as string);
  const batch = data?.object;

  if (isLoading) {
    return (
      <div className='flex h-64 items-center justify-center'>
        <div className='h-8 w-8 animate-spin rounded-full border-2 border-[rgb(237,224,74)] border-t-transparent' />
      </div>
    );
  }

  if (!batch) {
    return (
      <div className='flex min-h-[400px] flex-col items-center justify-center gap-4'>
        <p className='text-[15px] text-[rgb(115,123,140)]'>
          {String(t('advance_cod_batch_not_found'))}
        </p>
        <button
          type='button'
          onClick={() => navigate(`${ROUTE.FINANCE}?tab=${FinanceTab.AdvanceCod}`)}
          className='flex cursor-pointer items-center gap-2 rounded-lg border border-solid border-[rgb(232,230,227)] bg-white px-4 py-2 text-[13px] font-semibold text-[rgb(57,57,60)] transition-colors hover:bg-[rgb(245,245,243)]'
        >
          {String(t('advance_cod_back'))}
        </button>
      </div>
    );
  }

  const excludedCount = batch.excluded_orders_count ?? 0;

  const handleCopyId = () => {
    navigator.clipboard.writeText(batch.platform_code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const scrollToExcluded = () => {
    setActiveSegment(0);
    setTimeout(() => excludedNoticeRef.current?.scrollIntoView({ block: 'center' }), 50);
  };

  const gross = Number(batch.gross_items_amount ?? 0);
  const fees = Number(batch.total_delivery_fees ?? 0);
  const net = Number(batch.net_batch_amount ?? 0);

  return (
    <div className='relative flex flex-col gap-5 pb-24'>
      {/* sub-header */}
      <div className='flex items-start justify-between px-1'>
        <div>
          <div className='flex items-center gap-2'>
            <h2 className='m-0 text-xl font-bold tracking-tight text-[rgb(57,57,60)]'>
              <bdi>{batch.platform_code}</bdi>
            </h2>
            <button
              type='button'
              onClick={handleCopyId}
              className='cursor-pointer rounded-md border-0 bg-transparent p-1 transition-colors hover:bg-[rgb(245,245,243)]'
              title={copied ? String(t('copied')) : String(t('copy'))}
            >
              <Copy className='h-4 w-4 text-[rgb(115,123,140)]' />
            </button>
          </div>
          <span className='text-[12px] text-[rgb(115,123,140)]'>
            <bdi>{formatDateByDayjs(batch.created_at, 'D MMM YYYY, HH:mm')}</bdi>
          </span>
        </div>
        <BatchStatusPill status={batch.status} excludedCount={excludedCount} />
      </div>

      {/* lifecycle stepper (v2.1) */}
      <BatchLifecycleStepper batch={batch} onPartialBadgeClick={scrollToExcluded} />

      {/* segment tabs */}
      <div className='flex w-full border-b border-solid border-[rgb(232,230,227)]'>
        {[String(t('details')), String(t('orders'))].map((label, index) => (
          <button
            key={label}
            type='button'
            onClick={() => setActiveSegment(index)}
            className={`-mb-px cursor-pointer border-0 border-b-2 border-solid bg-transparent px-5 pb-2.5 pt-1 text-[13px] font-semibold transition-colors ${
              activeSegment === index
                ? 'border-b-[rgb(237,224,74)] text-[rgb(57,57,60)]'
                : 'border-b-transparent text-[rgb(115,123,140)] hover:text-[rgb(57,57,60)]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeSegment === 0 ? (
        <div className='flex flex-col gap-5'>
          {/* amount summary */}
          <section className='rounded-2xl border border-solid border-[rgb(232,230,227)] bg-white p-5'>
            <div className='mb-4 flex items-center gap-2'>
              <span className='text-[rgb(115,123,140)]'>
                <CircleDollarSign className='h-4 w-4' />
              </span>
              <h2 className='m-0 text-sm font-bold uppercase tracking-wider text-[rgb(57,57,60)]'>
                {String(t('advance_cod_batch_summary'))}
              </h2>
            </div>
            <div className='divide-y divide-[rgb(232,230,227)]'>
              <div className='flex items-start justify-between py-2'>
                <span className='text-sm text-[rgb(115,123,140)]'>
                  {String(t('advance_cod_gross_items_amount'))}
                </span>
                <span className='text-sm font-medium text-[rgb(57,57,60)]'>
                  <bdi>
                    {formatWithThousandSeparator(gross)} {String(t('iqd'))}
                  </bdi>
                </span>
              </div>
              <div className='flex items-start justify-between py-2'>
                <span className='text-sm text-[rgb(115,123,140)]'>
                  {String(t('advance_cod_delivery_fees'))}
                </span>
                {/* debit mode: leading −, dropped entirely at 0 (QA 6/12) */}
                <SignedAmount
                  amount={fees}
                  mode='debit'
                  className='text-sm font-medium text-[rgb(115,123,140)]'
                />
              </div>
              <div className='flex items-baseline justify-between py-2.5'>
                <span className='text-sm font-bold text-[rgb(57,57,60)]'>
                  {String(t('advance_cod_net_amount'))}
                </span>
                <span className='text-[24px] font-bold leading-none text-[rgb(57,57,60)]'>
                  <bdi>
                    {formatWithThousandSeparator(net)}{' '}
                    <span className='text-sm font-medium text-[rgb(115,123,140)]'>
                      {String(t('iqd'))}
                    </span>
                  </bdi>
                </span>
              </div>
            </div>
          </section>

          <DisbursementCard batch={batch} />
          <UpcomingAdjustmentCard batch={batch} />

          {/* excluded orders notice — partially_approved only */}
          {batch.status === 'partially_approved' && excludedCount > 0 ? (
            <div
              ref={excludedNoticeRef}
              className='flex items-start gap-2.5 rounded-xl border border-solid border-amber-200 bg-amber-50 px-4 py-3'
              data-testid='excluded-orders-notice'
            >
              <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-amber-500' />
              <p className='m-0 text-[13px] text-amber-700'>
                {String(t('advance_cod_excluded_notice', { count: excludedCount }))}{' '}
                <button
                  type='button'
                  onClick={() => {
                    // QA defect 18: opens the Orders tab pre-filtered to
                    // the excluded rows.
                    setExcludedOnly(true);
                    setActiveSegment(1);
                  }}
                  className='cursor-pointer border-0 bg-transparent p-0 text-[13px] font-semibold text-amber-700 underline'
                >
                  {String(t('advance_cod_view_excluded_orders'))}
                </button>
              </p>
            </div>
          ) : null}
        </div>
      ) : (
        <BatchOrdersPaginated
          uid={String(uid)}
          excludedCount={excludedCount}
          excludedOnly={excludedOnly}
          onExcludedOnlyChange={setExcludedOnly}
        />
      )}
    </div>
  );
};

export default AdvanceBatchSingle;
